import React, { useState, useEffect, useRef, useMemo } from 'react';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Autocomplete,
    CircularProgress,
} from '@mui/material';
import Axios from 'axios';
import { Utils } from '../../../utils/Utils';

const ModalPostoEscala = ({ open, onClose, onSubmit, profile, tokenChat, activeAusenciaData = {} }) => {
    const [postoOptions, setPostoOptions] = useState([]);
    const [escalaOptions, setEscalaOptions] = useState([]);
    const [loadingPosto, setLoadingPosto] = useState(false);
    const [loadingEscala, setLoadingEscala] = useState(false);

    const [selectedPosto, setSelectedPosto] = useState(null);
    const [selectedEscala, setSelectedEscala] = useState(null);
    const [postoSearchTerm, setPostoSearchTerm] = useState('');
    const [escalaSearchTerm, setEscalaSearchTerm] = useState('');

    const [nome, setNome] = useState('');
    const [re, setRe] = useState('');

    const postoTimeoutRef = useRef(null);
    const escalaTimeoutRef = useRef(null);

    const apiCHAT = useMemo(
        () =>
            Axios.create({
                baseURL:
                    process.env.NODE_ENV !== 'development'
                        ? process.env.REACT_APP_CHATBOT_API_URL_PRODUCTION
                        : process.env.REACT_APP_CHATBOT_API_URL_DEVELOPMENT,
            }),
        [],
    );

    useEffect(() => {
        if (open) {
            setNome(profile?.pess_nome || '');
            setRe(profile?.re || '');
        } else {
            setSelectedPosto(null);
            setSelectedEscala(null);
            setPostoSearchTerm('');
            setEscalaSearchTerm('');
            setPostoOptions([]);
            setEscalaOptions([]);
        }
    }, [open, profile]);

    useEffect(() => {
        if (!open || !tokenChat) return;

        const fetchPostos = async () => {
            setLoadingPosto(true);

            //buscar

            try {
                const response = await apiCHAT.get(`/postos-locais/seek?descricao=${postoSearchTerm || ''}`, {
                    headers: { Authorization: `Bearer ${tokenChat}` },
                });

                const data = Array.isArray(response.data) ? response.data : response.data.data || [];
                setPostoOptions(data);
            } catch (error) {
                console.error('Erro ao buscar postos:', error);
                setPostoOptions([]);
            } finally {
                setLoadingPosto(false);
            }
        };

        if (postoTimeoutRef.current) clearTimeout(postoTimeoutRef.current);
        postoTimeoutRef.current = setTimeout(() => {
            fetchPostos();
        }, 500);

        return () => clearTimeout(postoTimeoutRef.current);
    }, [postoSearchTerm, open, apiCHAT, tokenChat]);

    const clienteId = profile?.pess_id_cliente_principal;

    useEffect(() => {
        if (!open || !apiCHAT || !tokenChat) return;

        const fetchEscalas = async () => {
            setLoadingEscala(true);
            try {
                const response = await apiCHAT.get(
                    `/senior-escalas/list?buscar=${escalaSearchTerm || ''}&cliente_id=${clienteId ?? null}`,
                    {
                        headers: { Authorization: `Bearer ${tokenChat}` },
                    },
                );
                const data = Array.isArray(response.data) ? response.data : response.data.data || [];
                setEscalaOptions(data);
            } catch (error) {
                console.error('Erro ao buscar escalas:', error);
                setEscalaOptions([]);
            } finally {
                setLoadingEscala(false);
            }
        };

        if (escalaTimeoutRef.current) clearTimeout(escalaTimeoutRef.current);
        escalaTimeoutRef.current = setTimeout(() => {
            fetchEscalas();
        }, 500);

        return () => clearTimeout(escalaTimeoutRef.current);
    }, [escalaSearchTerm, open, apiCHAT, tokenChat, clienteId]);

    const handleSubmit = () => {
        if (selectedPosto && selectedEscala) {
            const detailed = selectedPosto;
            const pessoa = selectedEscala;
            onSubmit({
                nome_posto: detailed?.descricao || activeAusenciaData?.nome_posto || '',
                escala_posto: selectedEscala.nome_escala,
                dh_ausencia: activeAusenciaData?.dh_ausencia || '0000-00-00T00:00:00',
                dh_ausencia_final: activeAusenciaData?.dh_ausencia_final || '0000-00-00T00:00:00',
                nome_funcionario:
                    nome ||
                    profile?.pess_nome ||
                    activeAusenciaData?.nome_funcionario ||
                    activeAusenciaData?.pess_nome ||
                    '',
                codigo: detailed?.codigo ? detailed.codigo.toString() : activeAusenciaData?.codigo || '',
                pess_nome: nome || profile?.pess_nome || activeAusenciaData?.pess_nome || '',
                re: re || pessoa?.matricula?.toString() || profile?.re || activeAusenciaData?.re || '',
                usuario_criacao: profile?.pess_id_pessoa,
                cliente_id: profile?.pess_id_cliente_principal ?? null,
                status_solicitacao: Utils.calculateStatusSolicitacao(
                    activeAusenciaData?.dh_ausencia || '0000-00-00T00:00:00',
                ),
                codigo_posto: detailed?.codigo?.toString() || activeAusenciaData?.codigo || '',
                bairro: detailed?.bairro || activeAusenciaData?.bairro || '',
                cep: detailed?.cep || activeAusenciaData?.cep || '',
                cidade: detailed?.cidade || activeAusenciaData?.cidade || '',
                endereco: detailed?.endereco || activeAusenciaData?.endereco || '',
                endereco_complemento: detailed?.endereco_complemento || activeAusenciaData?.endereco_complemento || '',
                endereco_numero: detailed?.endereco_numero || activeAusenciaData?.endereco_numero || '',
                nome_gestor_posto: detailed?.nome_gestor_posto || activeAusenciaData?.nome_gestor_posto || '',
                normas_importantes_posto:
                    detailed?.normas_importantes_posto || activeAusenciaData?.normas_importantes_posto || '',
                perfil: detailed?.perfil || activeAusenciaData?.perfil || '',
                prioridade: Utils.calculatePrioridade(activeAusenciaData?.dh_ausencia || '0000-00-00T00:00:00'),
                procedimentos_especificos_posto:
                    detailed?.procedimentos_especificos_posto ||
                    activeAusenciaData?.procedimentos_especificos_posto ||
                    '',
                uf: detailed?.uf || activeAusenciaData?.uf || '',
                descricao: detailed?.descricao || '',
            });
        }
    };

    const isSubmitDisabled = !selectedPosto || !selectedEscala || !nome.trim() || !re.trim();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Informações do Posto e Escala</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <TextField label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} fullWidth />
                    <TextField label="Matrícula" value={re} onChange={(e) => setRe(e.target.value)} fullWidth />
                    <Autocomplete
                        options={postoOptions}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option?.descricao || '';
                        }}
                        loading={loadingPosto}
                        value={selectedPosto}
                        onChange={(event, newValue) => setSelectedPosto(newValue)}
                        onInputChange={(event, newInputValue) => setPostoSearchTerm(newInputValue)}
                        renderOption={(props, option) => {
                            const desc = option?.descricao || '';
                            const key = option?.codigo ? option.codigo.toString() : Math.random().toString();
                            return (
                                <li {...props} key={key}>
                                    {option?.codigo ? `${option.codigo} - ${desc}` : desc}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Posto de Trabalho"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingPosto ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <Autocomplete
                        options={escalaOptions}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option?.nome_escala || option?.descricao || '';
                        }}
                        loading={loadingEscala}
                        value={selectedEscala}
                        onChange={(event, newValue) => setSelectedEscala(newValue)}
                        onInputChange={(event, newInputValue) => setEscalaSearchTerm(newInputValue)}
                        renderOption={(props, option) => {
                            const desc = option?.nome_escala || option?.descricao || '';
                            const key = option?.codigo || option?.id || desc || Math.random().toString();
                            return (
                                <li {...props} key={key}>
                                    {option?.codigo ? `${option.codigo} - ${desc}` : desc}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Escala de Trabalho"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingEscala ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancelar
                </Button>
                <Button onClick={handleSubmit} color="primary" variant="contained" disabled={isSubmitDisabled}>
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalPostoEscala;
